import React, { useEffect, useState } from 'react';
import LoadingState from '../../components/LoadingState';
import useQueryParams from '../../hooks/useQueryParams';
import alertService from '../../services/AlertService';
import windowService from '../../services/windowService';
import { logger } from '../../utils/logger';
import { useUser } from '../authentication';
import { createOrgAccount } from '../org';
import { ErrorMessage } from '../status';
import { getAssociatedChurchAccounts } from './api/getAssociatedChurchAccounts';
import { getChurchAccountRedirect, clearChurchAccountRedirect } from './state/churchAccountRedirect';

export const MLC_REDIRECT_PARAM = 'orgid';

export const ConnectChurchAccount = () => {
  const { user } = useUser();
  const query = useQueryParams({ preserveParams: true });
  const [error, setError] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    const newlyCreatedChurchAccountId = query[MLC_REDIRECT_PARAM];

    if (!newlyCreatedChurchAccountId) {
      logger.error('Org ID not found in query parameters');
      setError('Org ID not found');
      return;
    }

    const redirect = getChurchAccountRedirect();
    clearChurchAccountRedirect();

    const action = redirect?.action ?? 'create';
    const redirectPath = redirect?.redirectPath ?? '/org';

    if (action === 'link') {
      windowService.redirectTo(`${redirectPath}?churchAccountId=${newlyCreatedChurchAccountId}`);
      return;
    }

    getAssociatedChurchAccounts().then(lifewayChurchAccounts => {
      if (cancelled) return;

      if (!user?.userId) {
        logger.error('User not found', { orgId: newlyCreatedChurchAccountId });
        setError('User not found');
        return;
      }

      const matchedAccount = lifewayChurchAccounts.find(
        account => account.churchAccountId === newlyCreatedChurchAccountId
      );
      if (!matchedAccount) {
        logger.error('No associated church account found', { orgId: newlyCreatedChurchAccountId, userId: user.userId });
        setError('No associated church account found with the provided orgId');
        return;
      }

      createOrgAccount(matchedAccount.churchAccountId, matchedAccount.name, user.userId, matchedAccount.churchAccountId)
        .then(() => {
          if (cancelled) return;
          alertService.showOnNextPage('Church Created');
          windowService.redirectTo(redirectPath);
        })
        .catch(error => {
          if (cancelled) return;
          logger.error('Error creating church account', { error });
          setError('An error occurred while creating the church account.');
        });
    });

    return () => {
      cancelled = true;
    };
  }, []);

  if (error) return <ErrorMessage />;
  return <LoadingState>Setting up your church</LoadingState>;
};
